/**
 * Delete Budget Confirmation Modal
 *
 * Displays a confirmation dialog when user attempts to delete a budget.
 * Uses the standard ConfirmDeleteModal for consistency with other delete operations.
 *
 * @component
 */

import React from 'react'
import ConfirmDeleteModal from '../../../components/Modal/ConfirmDeleteModal'

interface DeleteBudgetConfirmationModalProps {
  open: boolean
  budgetName: string
  onClose: () => void
  onConfirm: () => void
  isDeleting: boolean
}

/**
 * Confirmation modal for budget deletion
 * @param open - Controls modal visibility
 * @param budgetName - Name of budget being deleted
 * @param onClose - Called when modal is closed/cancelled
 * @param onConfirm - Called when user confirms deletion
 * @param isDeleting - Whether deletion is in progress (unused but kept for backward compatibility)
 */
const DeleteBudgetConfirmationModal: React.FC<DeleteBudgetConfirmationModalProps> = ({
  open,
  budgetName,
  onClose,
  onConfirm,
  isDeleting
}) => {
  const handleUserInput = (action: 'cancel' | 'delete') => {
    if (action === 'delete') {
      onConfirm()
    } else {
      onClose()
    }
  }

  return (
    <ConfirmDeleteModal
      open={open}
      userInput={handleUserInput}
      customDeleteMsg={`Are you sure you want to delete "${budgetName}"? This action cannot be undone. All budget data, positions, and calculations will be permanently deleted.`}
    />
  )
}

export default DeleteBudgetConfirmationModal
